'use client';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import Image from '@repo/ui/src/components/Image/New';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { ArticleModuleProps } from '@repo/ui/src/types/articleModules';
import { MediaType } from '@repo/ui/src/types/image';
import { YoutubeLinkProps } from '@repo/ui/src/types/youtubeLink';
import NavList from './_NavList';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import React, { useState } from 'react';
import YoutubeVideo from '@repo/ui/src/components/YoutubeVideo';
import ArticleModule from '@repo/ui/src/components/ArticleModule';
import InlineRecipe, { InlineRecipeProps } from '@components/InlineRecipe';
import { useIsMD } from '@repo/ui/src/utils/useMediaQuery';

export interface HeaderAsset {
  image: MediaType;
  youtubeLink: YoutubeLinkProps;
}
interface ContentProps {
  headerAsset?: Array<HeaderAsset>;
  navigation: boolean;
  title: string;
  description: string | null;
  recipe?: InlineRecipeProps;
  articleModules?: Array<ArticleModuleProps>;
}

const Content = ({
  headerAsset,
  articleModules,
  navigation,
  title,
  description,
  recipe,
}: ContentProps) => {
  const { t } = useTranslation();
  const mdAndUp = useIsMD();
  const [visibleSection, setVisibleSection] = useState(0);

  if (!navigation && !articleModules?.length) return null;

  return (
    <>
      {navigation && (
        <aside className="tw-bg-warm-200 after:tw-bg-warm-200 tw-grid md:tw-grid-cols-[auto,1fr] tw-gap-10 tw-px-10 tw-pt-9 tw-pb-6 md:tw-pr-10 md:tw-pl-16 lg:tw-pl-20">
          {!mdAndUp && (
            <div className="tw-grid tw-gap-y-3">
              <h1 className="tra-text-web-heading-2 tw-text-center">{title}</h1>
              {recipe && (
                <Button
                  variant={ButtonVariant.skewer}
                  className="tw-justify-self-center"
                  as={Anchor}
                  link="#inline-recipe-container"
                >
                  {t('recipeContent.jumpToRecipeLabel', undefined, { default: 'Jump To Recipe' })}
                </Button>
              )}
            </div>
          )}
          {articleModules && (
            <NavList
              navItems={articleModules}
              visibleSection={visibleSection}
              setVisibleSection={setVisibleSection}
            />
          )}
        </aside>
      )}
      <article className="md:tw-pt-20 tw-scroll-smooth tw-mb-9 md:tw-mb-20">
        <div className="tw-grid tw-var-[px-40px] md:tw-var-[px-60px] lg:tw-var-[px-80px] tw-grid-cols-[minmax(var(--px),1fr)_minmax(0,750px)_minmax(var(--px),1fr)] tw-gap-y-20">
          <header className="tw-grid tw-gap-y-8 tw-grid-cols-subgrid tra-layout-center">
            {(mdAndUp || !navigation) && (
              <h1 className="tra-text-web-heading-2 tw-text-center tw-pt-10 md:tw-pt-0">{title}</h1>
            )}
            {description && <p className="tra-text-web-body-xl">{description}</p>}
            {recipe && (
              <Button
                variant={ButtonVariant.skewer}
                className="tw-hidden md:tw-inline-block tw-justify-self-center"
                as={Anchor}
                link="#inline-recipe-container"
              >
                {t('recipeContent.jumpToRecipeLabel', undefined, { default: 'Jump To Recipe' })}
              </Button>
            )}
            {headerAsset?.[0] && (
              <>
                {headerAsset[0].image && (
                  <div className="tra-full-bleed">
                    <Image
                      className="tw-w-full tw-object-fit tw-object-cover"
                      src={headerAsset[0].image.url!}
                      alt={headerAsset[0].image.name!}
                      height={500}
                      width={768}
                    />
                  </div>
                )}
                {headerAsset[0].youtubeLink && (
                  <div className="tw-flex tw-content-center tra-full-bleed">
                    <YoutubeVideo {...headerAsset[0].youtubeLink} />
                  </div>
                )}
              </>
            )}
          </header>
          <div className="tw-px-10 md:tw-px-0 tw-grid-cols-subgrid tra-layout-center">
            {articleModules?.map((articleModule, index) => (
              <React.Fragment key={index}>
                {!!index && articleModule.headerText && (
                  <hr className="tw-my-15 tw-bg-warm-400" />
                )}
                <ArticleModule
                  {...articleModule}
                  index={index}
                  setVisibleSection={setVisibleSection}
                />
              </React.Fragment>
            ))}
            {recipe && <InlineRecipe {...recipe} />}
          </div>
        </div>
      </article>
    </>
  );
};

export default Content;
